import { useMemo, useRef, useState } from 'react';
import { AlertCircle, UserPlus, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useClusters,
  useFarms,
  useMe,
  useTeam,
  useUnblockMember,
} from '@/pages/team/hooks/useTeamQueries';
import { deriveMemberStatus, grantableRoles } from '@/pages/team/teamLogic';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { sessionFarmIdOf } from '@/components/scope/sessionFarm';
import type { Invitation, UserMember } from '@/pages/team/types';
import { InviteDrawer } from '@/pages/team/components/InviteDrawer';
import { ChangeAccessDrawer } from '@/pages/team/components/ChangeAccessDrawer';
import { BlockMemberDialog } from '@/pages/team/components/BlockMemberDialog';
import { ArchiveMemberDialog } from '@/pages/team/components/ArchiveMemberDialog';
import { RevokeInviteDialog } from '@/pages/team/components/RevokeInviteDialog';
import { MemberRow } from '@/pages/team/components/MemberRow';
import { PendingInviteRow } from '@/pages/team/components/PendingInviteRow';

/** Заголовки таблицы участников (переиспользуются секцией приглашений). */
function TeamTableHead({ lastCol }: { lastCol: string }) {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>Имя / Email</TableHead>
        <TableHead>Роль</TableHead>
        <TableHead>Зона доступа</TableHead>
        <TableHead>Статус</TableHead>
        <TableHead>{lastCol}</TableHead>
        <TableHead className="text-right" />
      </TableRow>
    </TableHeader>
  );
}

function SectionLabel({ children, count }: { children: string; count: number }) {
  return (
    <div className="mb-2 mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <span>{children}</span>
      <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
        {count}
      </Badge>
    </div>
  );
}

/**
 * TeamPanel — содержимое экрана «Команда» (AK-341) без внешнего AppShell.
 *
 * Вынесено из TeamPage (AK-350) для переиспользования вкладкой раздела
 * «Компания» (/company/team) и самостоятельной страницей /team
 * (редиректится в /company/team). Логика без изменений.
 *
 * `showHeader={false}` — шапку даёт CompanyLayout, панель отдаёт только
 * тулбар действий + таблицы.
 */
export function TeamPanel({ showHeader = true }: { showHeader?: boolean }) {
  const me = useMe();
  const team = useTeam();
  const clustersQ = useClusters();
  const farmsQ = useFarms();

  const clusters = clustersQ.data ?? [];
  const farms = farmsQ.data ?? [];
  const sessionFarmId = sessionFarmIdOf(useAvailableScopes().data);

  // AK-886 (ADR-081 §4): что выдавать и куда — из ответа GET /users, не из
  // констант. Пока ответа нет — «Пригласить» скрыта (fail-closed); старый бэк
  // без поля — прежний полный список, кастомные роли как раньше.
  const grantable = grantableRoles(team.data?.can_invite_codes);
  const manageArea = team.data?.manage_area ?? null;
  const allowCustomRoles = team.data?.can_assign_custom_roles ?? true;
  const canInvite = team.data !== undefined && grantable.length > 0;

  const currentUserId = me.data?.user.id ?? null;

  const unblock = useUnblockMember();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [accessMember, setAccessMember] = useState<UserMember | null>(null);
  const [blockMember, setBlockMember] = useState<UserMember | null>(null);
  const [archiveMember, setArchiveMember] = useState<UserMember | null>(null);
  const [revokeInvite, setRevokeInvite] = useState<Invitation | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [highlightEmail, setHighlightEmail] = useState<string | null>(null);

  const pendingRef = useRef<HTMLDivElement | null>(null);

  const members = team.data?.members ?? [];
  const invitations = team.data?.pending_invitations ?? [];

  const activeCount = members.filter((m) => deriveMemberStatus(m) === 'active').length;
  const blockedCount = members.filter((m) => deriveMemberStatus(m) === 'blocked').length;
  const archivedCount = members.filter((m) => deriveMemberStatus(m) === 'archived').length;
  const inactiveCount = blockedCount + archivedCount;

  const visibleMembers = useMemo(
    () =>
      showInactive
        ? members
        : members.filter((m) => deriveMemberStatus(m) === 'active'),
    [members, showInactive],
  );

  /** Разблокировка — прямое действие (без confirm-диалога, обратимо). */
  const handleUnblock = (m: UserMember) => {
    unblock.mutate(m.member_id);
  };

  /** При 409 «уже приглашён» — подсветить и прокрутить к pending-строке. */
  const scrollToInvite = (email: string) => {
    setHighlightEmail(email.toLowerCase());
    requestAnimationFrame(() => {
      pendingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    window.setTimeout(() => setHighlightEmail(null), 2600);
  };

  const isLoading = team.isLoading || me.isLoading;
  const loadError = team.error;
  const isForbidden =
    (loadError as { status?: number } | null)?.status === 403 ||
    (me.error as { status?: number } | null)?.status === 403;

  const isEmpty =
    !isLoading && !loadError && members.length <= 1 && invitations.length === 0;

  return (
    <>
      {showHeader ? (
        <PageHeader
          title="Команда"
          showBack={false}
          description="Приглашайте сотрудников, назначайте роли и зоны доступа."
          actions={
            canInvite ? (
              <Button onClick={() => setInviteOpen(true)}>
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline">Пригласить</span>
              </Button>
            ) : undefined
          }
        />
      ) : (
        canInvite && (
          <div className="flex justify-end">
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Пригласить</span>
            </Button>
          </div>
        )
      )}

      {!isLoading && !loadError && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="font-normal">
            {activeCount} активных
          </Badge>
          {blockedCount > 0 && (
            <Badge variant="secondary" className="font-normal">
              {blockedCount} заблокировано
            </Badge>
          )}
          {archivedCount > 0 && (
            <Badge variant="secondary" className="font-normal">
              {archivedCount} в архиве
            </Badge>
          )}
          {invitations.length > 0 && (
            <Badge variant="secondary" className="font-normal">
              {invitations.length} приглашений
            </Badge>
          )}
          {inactiveCount > 0 && (
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={showInactive}
                onCheckedChange={(v) => setShowInactive(v === true)}
              />
              Показать заблокированных и архив
            </label>
          )}
        </div>
      )}

      {isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            У вас нет прав управлять командой. Обратитесь к администратору компании.
          </AlertDescription>
        </Alert>
      )}

      {loadError && !isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Не удалось загрузить команду. Обновите страницу.
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <Card className="mt-6 p-4">
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      )}

      {isEmpty && (
        <Card className="mt-6 px-6 py-12 text-center">
          <div className="mx-auto flex max-w-md flex-col items-center">
            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
              <Users className="h-7 w-7" />
            </span>
            <h3 className="text-lg font-semibold text-foreground">
              Пока вы работаете один
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Пригласите агрономов и наблюдателей — они получат доступ к полям,
              картам и осмотрам в своей зоне.
            </p>
            {canInvite && (
              <Button
                variant="accent-brand"
                className="mt-6"
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus className="h-4 w-4" />
                Пригласить первого участника
              </Button>
            )}
          </div>
        </Card>
      )}

      {!isLoading && !loadError && !isEmpty && (
        <>
          {invitations.length > 0 && (
            <div ref={pendingRef}>
              <SectionLabel count={invitations.length}>Приглашения</SectionLabel>
              <Card className="overflow-hidden">
                {/* overflow-x — таблица не ломает layout на узких вьюпортах */}
                <div className="overflow-x-auto">
                  <Table className="min-w-[720px]">
                    <TeamTableHead lastCol="Осталось" />
                    <TableBody>
                      {invitations.map((inv) => (
                        <PendingInviteRow
                          key={inv.id}
                          invitation={inv}
                          onRevoke={setRevokeInvite}
                          revoking={false}
                          highlighted={
                            highlightEmail === inv.email.toLowerCase()
                          }
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </div>
          )}

          <SectionLabel count={visibleMembers.length}>Участники</SectionLabel>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <TeamTableHead lastCol="Последний вход" />
                <TableBody>
                  {visibleMembers.map((m) => (
                    <MemberRow
                      key={m.member_id}
                      member={m}
                      currentUserId={currentUserId}
                      onChangeAccess={setAccessMember}
                      onBlock={setBlockMember}
                      onUnblock={handleUnblock}
                      onArchive={setArchiveMember}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      <InviteDrawer
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        clusters={clusters}
        farms={farms}
        onViewExisting={scrollToInvite}
        roles={grantable}
        area={manageArea}
        sessionFarmId={sessionFarmId}
      />
      <ChangeAccessDrawer
        member={accessMember}
        open={accessMember !== null}
        onOpenChange={(o) => !o && setAccessMember(null)}
        clusters={clusters}
        farms={farms}
        roles={grantable}
        area={manageArea}
        allowCustom={allowCustomRoles}
      />
      <BlockMemberDialog
        member={blockMember}
        open={blockMember !== null}
        onOpenChange={(o) => !o && setBlockMember(null)}
      />
      <ArchiveMemberDialog
        member={archiveMember}
        open={archiveMember !== null}
        onOpenChange={(o) => !o && setArchiveMember(null)}
      />
      <RevokeInviteDialog
        invitation={revokeInvite}
        open={revokeInvite !== null}
        onOpenChange={(o) => !o && setRevokeInvite(null)}
      />
    </>
  );
}

export default TeamPanel;
